import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Paperclip, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { useToast } from "@/hooks/use-toast";
import {
  CATEGORY_LABELS,
  collectReportMetadata,
  feedbackApi,
  type ReportCategory,
} from "@/services/feedback-api.service";

const MAX_FILES = 5;
const MAX_SIZE_MB = 10;
const MAX_SIZE = MAX_SIZE_MB * 1024 * 1024;

type ReportBugDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type PendingFile = {
  id: string;
  file: File;
  previewUrl?: string;
};

export function ReportBugDialog({ open, onOpenChange }: ReportBugDialogProps) {
  const { toast } = useToast();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<ReportCategory>("bug");
  const [files, setFiles] = useState<PendingFile[]>([]);

  const reset = useCallback(() => {
    setTitle("");
    setDescription("");
    setCategory("bug");
    setFiles((prev) => {
      prev.forEach((f) => f.previewUrl && URL.revokeObjectURL(f.previewUrl));
      return [];
    });
  }, []);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const descLeft = Math.max(0, 20 - description.trim().length);
  const titleOk = title.trim().length >= 3;
  const descOk = description.trim().length >= 20;
  const canSubmit = titleOk && descOk;

  const addFiles = useCallback(
    (list: FileList | File[]) => {
      const incoming = Array.from(list);
      setFiles((prev) => {
        const next = [...prev];
        for (const file of incoming) {
          if (next.length >= MAX_FILES) {
            toast({
              title: "Лимит вложений",
              description: `Не более ${MAX_FILES} файлов`,
              variant: "destructive",
            });
            break;
          }
          if (file.size > MAX_SIZE) {
            toast({
              title: "Файл слишком большой",
              description: `${file.name}: максимум ${MAX_SIZE_MB} МБ`,
              variant: "destructive",
            });
            continue;
          }
          const previewUrl = file.type.startsWith("image/")
            ? URL.createObjectURL(file)
            : undefined;
          next.push({ id: `${file.name}-${file.size}-${Date.now()}`, file, previewUrl });
        }
        return next;
      });
    },
    [toast],
  );

  useEffect(() => {
    if (!open) return;
    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const pasted: File[] = [];
      for (const item of Array.from(items)) {
        if (item.type.startsWith("image/")) {
          const f = item.getAsFile();
          if (f) pasted.push(f);
        }
      }
      if (pasted.length) {
        e.preventDefault();
        addFiles(pasted);
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [open, addFiles]);

  const submitMutation = useMutation({
    mutationFn: async () => {
      const report = await feedbackApi.createReport({
        title: title.trim(),
        description: description.trim(),
        category,
        app_version: "web",
        metadata: collectReportMetadata(),
      });
      for (const pf of files) {
        await feedbackApi.uploadAttachment(report.id, pf.file);
      }
      return report;
    },
    onSuccess: () => {
      toast({ title: "Отчёт отправлен", description: "Спасибо за обращение" });
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error ? err.message : "Не удалось отправить отчёт";
      toast({ title: "Ошибка", description: message, variant: "destructive" });
    },
  });

  const categoryOptions = useMemo(
    () => (Object.keys(CATEGORY_LABELS) as ReportCategory[]).map((code) => ({
      code,
      label: CATEGORY_LABELS[code],
    })),
    [],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="z-[230] max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-1.5">
            <DialogTitle>Сообщить о проблеме</DialogTitle>
            <SectionHelpTooltip label="Справка по обратной связи">
              <p className="font-medium">Зачем</p>
              <p>Отправьте отчёт об ошибке или проблеме в интерфейсе.</p>
              <p className="mt-2 font-medium">Как работать</p>
              <ul className="list-disc space-y-1 pl-4">
                <li>Заголовок ≥ 3 символа, описание ≥ 20.</li>
                <li>Можно приложить до {MAX_FILES} файлов (скриншоты, PDF, текст), до {MAX_SIZE_MB} МБ.</li>
                <li>Скриншот из буфера: Ctrl+V / Cmd+V.</li>
              </ul>
              <p className="mt-2 text-muted-foreground">
                Страница и тип устройства подставятся автоматически.
              </p>
            </SectionHelpTooltip>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="space-y-2">
            <Label htmlFor="feedback-title">Заголовок</Label>
            <Input
              id="feedback-title"
              value={title}
              maxLength={255}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Кратко, что случилось"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="feedback-desc">Описание</Label>
              {!descOk && (
                <span className="text-xs text-muted-foreground">ещё {descLeft}</span>
              )}
            </div>
            <Textarea
              id="feedback-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              placeholder="Опишите шаги воспроизведения и ожидаемое поведение"
            />
            <p className="text-xs text-muted-foreground">
              Страница и тип устройства подставятся автоматически.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Категория</Label>
            <Select
              value={category}
              onValueChange={(v) => setCategory(v as ReportCategory)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[250]">
                {categoryOptions.map((opt) => (
                  <SelectItem key={opt.code} value={opt.code}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Вложения</Label>
            <div
              className="rounded-md border border-dashed p-3 text-sm text-muted-foreground"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
              }}
            >
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const input = document.createElement("input");
                    input.type = "file";
                    input.multiple = true;
                    input.accept = "image/*,application/pdf,text/plain";
                    input.onchange = () => {
                      if (input.files?.length) addFiles(input.files);
                    };
                    input.click();
                  }}
                >
                  <Paperclip className="mr-1.5 h-4 w-4" />
                  Выбрать файлы
                </Button>
                <span>или перетащите / вставьте скриншот (Ctrl+V)</span>
              </div>
              {files.length > 0 && (
                <ul className="mt-3 space-y-2">
                  {files.map((pf) => (
                    <li
                      key={pf.id}
                      className="flex items-center gap-2 rounded bg-muted/40 px-2 py-1.5 text-foreground"
                    >
                      {pf.previewUrl ? (
                        <img
                          src={pf.previewUrl}
                          alt=""
                          className="h-10 w-10 rounded object-cover"
                        />
                      ) : (
                        <Paperclip className="h-4 w-4 shrink-0" />
                      )}
                      <span className="min-w-0 flex-1 truncate text-xs">{pf.file.name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        aria-label="Убрать файл"
                        onClick={() =>
                          setFiles((prev) => {
                            const target = prev.find((x) => x.id === pf.id);
                            if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
                            return prev.filter((x) => x.id !== pf.id);
                          })
                        }
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button
            type="button"
            disabled={!canSubmit || submitMutation.isPending}
            onClick={() => submitMutation.mutate()}
          >
            {submitMutation.isPending ? "Отправка…" : "Отправить"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
